import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { components } from '@/api/openapi'
import { useSSE, type SSEState } from '@/lib/sse'
import { useReportLiveState } from '@/components/live-status-context'

type Run = components['schemas']['Run']
type RunStatus = components['schemas']['RunStatus']

type RunUpdatedPayload = Partial<Run> & {
  run_id: string
  status?: RunStatus
}

function isRunUpdatedPayload(value: unknown): value is RunUpdatedPayload {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return typeof v.run_id === 'string'
}

export function useRunsLive(): { liveState: SSEState } {
  const queryClient = useQueryClient()

  const onEvent = useCallback(
    (eventName: string, data: unknown) => {
      if (eventName !== 'run.updated') return
      if (!isRunUpdatedPayload(data)) return
      void queryClient.invalidateQueries({ queryKey: ['get', '/runs'] })
      const detailKeyPrefix = ['get', '/runs/{run_id}'] as const
      queryClient.setQueryData<Run | undefined>(
        [...detailKeyPrefix, { params: { path: { run_id: data.run_id } } }],
        (prev) => (prev ? { ...prev, ...data } : prev),
      )
    },
    [queryClient],
  )

  const topics = useMemo(() => ['runs'], [])
  const { state } = useSSE(topics, { onEvent })
  useReportLiveState(state)
  return { liveState: state }
}
